import { useCallback, useEffect, useRef } from "react";
import { useFetch } from "@/hooks/useFetch";
import { useLang } from "@/contexts/LangContext";
import { useUser } from "@/contexts/userContext";
import { isSupportedLanguage } from "@/i18n";
import { ENDPOINTS } from "@/const/endpoints";

// Keeps the UI language and the account's saved language in step: a saved choice wins once per
// sign-in (so polling /me cannot undo a switch still in flight); an account without one adopts
// the language the visitor is already using.
export function useLanguagePreference() {
  const { lang, setLang } = useLang();
  const { user, setUser } = useUser();
  const fetchClient = useFetch();
  const syncedUserId = useRef(null);
  const langRef = useRef(lang);
  langRef.current = lang;

  const saveToAccount = useCallback(
    (code) =>
      fetchClient
        .patch(ENDPOINTS.ME_LANGUAGE, { language: code }, { skipNetworkErrorToast: true })
        .then((res) => setUser((prev) => (prev ? { ...prev, language: res.data.language } : prev)))
        .catch((e) => console.error("Failed to save language:", e)),
    [fetchClient, setUser],
  );

  const userId = user?._id ?? null;
  const savedLanguage = user?.language ?? null;

  useEffect(() => {
    if (!userId) {
      syncedUserId.current = null;
      return;
    }
    if (syncedUserId.current === userId) return;
    syncedUserId.current = userId;
    if (isSupportedLanguage(savedLanguage)) {
      if (savedLanguage !== langRef.current) setLang(savedLanguage);
    } else {
      saveToAccount(langRef.current);
    }
  }, [userId, savedLanguage, setLang, saveToAccount]);

  const selectLanguage = useCallback(
    (code) => {
      setLang(code);
      if (userId) saveToAccount(code);
    },
    [setLang, userId, saveToAccount],
  );

  return { lang, selectLanguage };
}
